import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AccountContext from '../../../contexts/AccountContext';
import UNIT_LOCALES from '../../../propTypes/unitLocales';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import STORE from '../../../store';

const DEMO_6_ACCOUNT_ID = 'fopstBvqxkLZeqNvLTBM';
const SANDBOX_ACCOUNT_ID = 'fopsGfEtxXSRQKXyEqkN';

const DEFAULT_START_OF_WEEK = 'SUN';

function capitalizeFirstLetter(string: string) {
  if (!string) {
    return '';
  }
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const AccountProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { accountRef, selectedAccount, selectedAccountId } =
    useContext(AccountPickerContext);
  const [unitsLocale, setUnitsLocale] = useState<string>(UNIT_LOCALES.IMPERIAL);
  const [status, setStatus] = useState<
    'initialized' | 'pending' | 'active' | 'error' | undefined
  >(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoadingCarrierName, setIsLoadingCarrierName] =
    useState<boolean>(true);
  const [timezone, setTimezone] = useState<string | undefined>();
  const [weekStartsOn, setWeekStartsOn] = useState<WeekStartsOn>(
    DEFAULT_START_OF_WEEK,
  );
  const [driverScoreDataSet, setDriverScoreDataSet] = useState<
    string | undefined
  >();
  const [isTemplateAccount, setIsTemplateAccount] = useState<boolean>(false);
  const [isBillingSetUp, setIsBillingSetUp] = useState<boolean>(false);
  const [isIRSetUp, setIsIRSetUp] = useState<boolean>(false);
  const [configuredProviders, setConfiguredProviders] = useState<string[]>([]);
  const [tms, setTms] = useState<string | undefined>();
  const [adfRequired, setAdfRequired] = useState<boolean | undefined>();
  const [hasAppsEnabled, setHasAppsEnabled] = useState<boolean>(false);
  const [isV3UIEnabled, setIsV3UIEnabled] = useState<boolean>(false);
  const [isTopNavMenusHidden, setIsTopNavMenusHidden] =
    useState<boolean>(false);
  const [homeProductId, setHomeProductId] = useState<string | undefined>(
    undefined,
  );
  const [remindersPreferDriverCode, setRemindersPreferDriverCode] =
    useState<boolean>(false);
  const [aiBudgets, setAiBudgets] = useState<
    { perUserOverrideUsd?: { [userId: string]: number } } | undefined
  >(undefined);
  const [
    blockBasicUsersContactingSupport,
    setBlockBasicUsersContactingSupport,
  ] = useState<boolean>(false);
  const [driverAppEmbedCarrierSlug, setDriverAppEmbedCarrierSlug] = useState<
    string | undefined
  >(undefined);
  const [driverAppEmbedProgramSlug, setDriverAppEmbedProgramSlug] = useState<
    string | undefined
  >(undefined);
  const [isDemoAccount, setIsDemoAccount] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    STORE.getAccountRef({ accountId: selectedAccountId })
      .get()
      .then((doc) => {
        const account = doc.data();
        if (!account) {
          return;
        }
        setTms(account.tms);
        setAdfRequired(account.adfRequired);
        setIsLoadingCarrierName(false);
        setStatus(account.status ? account.status : 'active');
        setTimezone(account.timeZone);
        setIsTemplateAccount(!!account.isTemplateAccount);
        setHasAppsEnabled(!!account.hasAppsEnabled);
        setIsV3UIEnabled(!!account.isV3UIEnabled);
        setIsTopNavMenusHidden(!!account.isTopNavMenusHidden);
        setHomeProductId(account.homeProductId);
        setRemindersPreferDriverCode(!!account.remindersPreferDriverCode);
        setAiBudgets(account.aiBudgets);
        setBlockBasicUsersContactingSupport(
          !!account.blockBasicUsersContactingSupport,
        );
        setDriverAppEmbedCarrierSlug(account.driverAppEmbedCarrierSlug);
        setDriverAppEmbedProgramSlug(account.driverAppEmbedProgramSlug);
        setIsDemoAccount(!!account.isDemoAccount);
        setIsLoading(false);

        const maybeL = account.unitsLocale;
        if (maybeL) {
          setUnitsLocale(maybeL);
        } else {
          setUnitsLocale(UNIT_LOCALES.METRIC);
        }
      });

    const l2 = STORE.getAccountRef({ accountId: selectedAccountId }).onSnapshot(
      (snapshot) => {
        const accountData = snapshot.data();
        if (!accountData) {
          return;
        }
        setStatus(accountData.status);
        setError(accountData.error);
        setWeekStartsOn(accountData.startOfWeek || DEFAULT_START_OF_WEEK);
        setHasAppsEnabled(!!accountData.hasAppsEnabled);
        setIsV3UIEnabled(!!accountData.isV3UIEnabled);
        setIsTopNavMenusHidden(!!accountData.isTopNavMenusHidden);
        setHomeProductId(accountData.homeProductId);
        setRemindersPreferDriverCode(!!accountData.remindersPreferDriverCode);
        setAiBudgets(accountData.aiBudgets);
        setBlockBasicUsersContactingSupport(
          !!accountData.blockBasicUsersContactingSupport,
        );
        setDriverAppEmbedCarrierSlug(accountData.driverAppEmbedCarrierSlug);
        setDriverAppEmbedProgramSlug(accountData.driverAppEmbedProgramSlug);
        setIsDemoAccount(!!accountData.isDemoAccount);
      },
    );
    return () => {
      l2();
    };
  }, [accountRef, selectedAccountId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    accountRef
      .collection('scoring')
      .doc('drivers')
      .get()
      .then((driverScoringConfigDoc) => {
        if (driverScoringConfigDoc.exists) {
          const data = driverScoringConfigDoc.data();
          if (!data) {
            return;
          }
          if (data.period) {
            const newDataSet = `driverScore${capitalizeFirstLetter(
              data.period,
            )}`;
            setDriverScoreDataSet(newDataSet);
          } else {
            setDriverScoreDataSet('driverScores');
          }
        }
      });
  }, [accountRef]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    accountRef
      .collection('onBoarding')
      .doc('status')
      .onSnapshot((snap) => {
        if (snap.exists) {
          const data = snap.data();
          if (data) {
            setIsBillingSetUp(data.isBillingSetUp);
            setIsIRSetUp(!!data.isIRSetUp);
            setConfiguredProviders(
              data.configuredProviders || window.emptyArray,
            );
          }
        }
      });
  }, [accountRef]);

  const isSandboxAccount = accountRef && accountRef.id === SANDBOX_ACCOUNT_ID;
  const isDemoAccount6 = accountRef && accountRef.id === DEMO_6_ACCOUNT_ID;

  return (
    <AccountContext.Provider
      value={{
        unitsLocale,
        id: selectedAccount.accountId,
        carrierName: selectedAccount.accountName,
        status,
        error,
        isLoadingCarrierName,
        weekStartsOn,
        driverScoreDataSet,
        tms,
        adfRequired,
        isBillingSetUp,
        isIRSetUp,
        configuredProviders,
        timezone,
        isTemplateAccount,
        isLoading,
        isAccountOnBoarded: status !== 'active',
        isDemoAccount: isDemoAccount || isSandboxAccount,
        hasAppsEnabled,
        isDemo6: isDemoAccount6,
        isV3UIEnabled,
        isTopNavMenusHidden,
        homeProductId,
        remindersPreferDriverCode,
        blockBasicUsersContactingSupport,
        driverAppEmbedCarrierSlug,
        driverAppEmbedProgramSlug,
        aiBudgets,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

AccountProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
};

export default AccountProvider;
